"use client";

import type { SdkArtifactPickerItem } from "@magnus/contract/artifacts";
import type { MagnusUIMessage } from "@magnus/contract/messages";
import type { SdkSkillPickerItem } from "@magnus/contract/skills";
import type { TurnOptions } from "@magnus/contract/turn";
import type { ReasoningLevel } from "@magnus/contract/values";
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { type ConversationStore, createConversationStore, type MagnusChatIdPersistence } from "./conversation-store";
import { type MagnusLiveCallSource, useMagnusLiveCall } from "./live-call/use-magnus-live-call";
import { useMagnusClient } from "./provider";
import type { MagnusApprovalsSource, MagnusChatHistorySource, MagnusDictationSource, MagnusFeedbackSource, MagnusModelsSource, MagnusUploadsSource } from "./source-stores";
import { useMagnusApprovals, useMagnusArtifactPicker, useMagnusDictation, useMagnusFeedback, useMagnusModels, useMagnusSkillPicker, useMagnusUploads } from "./use-magnus-sources";
export interface UseMagnusConversationOptions {
  /**
   * Push-to-talk dictation in the composer (plan 027). Default on — the
   * mic button renders but mints nothing until first tapped. Set `false`
   * to keep the mic off this surface.
   */
  dictation?: boolean;
  /**
   * Live voice calls in the composer (plan 042). Default on — the call
   * button renders only once the server reports calls available for this
   * user. Set `false` to keep calls off this surface.
   */
  liveCall?: boolean;
  /**
   * Where the active chat id survives a reload (e.g. the
   * `FloatingUrlPersistence` returned by `createFloatingUrlPersistence()`,
   * which pins it to `?mchat=`). Omit for in-memory sessions.
   *
   * Referential stability is not required: the hook reads this through a
   * stable adapter, so an inline object won't restart the session. Only the
   * value present on the first render decides what gets restored, though —
   * the restore runs once, on mount.
   */
  persistence?: MagnusChatIdPersistence;
  /**
   * Host-owned turn options (workspace, capabilities, ...) merged into
   * every send. The conversation's model selection wins on `modelId` and
   * `reasoningLevel`;
   * everything else passes through untouched. Read at send time, so a
   * workspace switch applies to the next turn without a remount.
   *
   * Referential stability is not required: an inline object with unchanged
   * fields won't invalidate `chatProps`.
   */
  turnOptions?: Omit<TurnOptions, "modelId">;
}

/** Everything `<MagnusChat>` needs, ready to spread. */
export interface MagnusConversationChatProps {
  /** In-flight run id on a restored/selected chat (stream resume). */
  activeStreamId?: string;
  /** Resolves HITL approval pauses over `client.toolApprovals.submit`. */
  approvals: MagnusApprovalsSource;
  artifacts: {
    items: SdkArtifactPickerItem[];
    loading: boolean;
  };
  chatId?: string;
  /** Composer dictation source; present unless `dictation: false`. */
  dictation?: MagnusDictationSource;
  feedback?: MagnusFeedbackSource;
  history: MagnusChatHistorySource;
  initialMessages?: MagnusUIMessage[];
  /** Live voice call source; present while calls are available, unless
   *  `liveCall: false`. */
  liveCall?: MagnusLiveCallSource;
  models: MagnusModelsSource;
  onFinish: () => void;
  onFirstStream: () => void;
  /** Stops the run and persists the partial transcript. */
  onStop: (partialMessages: MagnusUIMessage[]) => Promise<void>;
  skills: {
    items: SdkSkillPickerItem[];
    loading: boolean;
  };
  turnOptions?: TurnOptions;
  /** Composer attachments source; `undefined` until a chat id exists. */
  uploads?: MagnusUploadsSource;
}
export interface UseMagnusConversationResult {
  /** The chat being driven — restored, selected, or the session's
   *  client-minted id (materialized server-side on first send). */
  chatId?: string;
  /** Spread onto `<MagnusChat>`: sources, transcript hydration, turn
   *  invalidation. Pass `sessionKey` as its `key` so switching chats
   *  remounts the transcript. */
  chatProps: MagnusConversationChatProps;
  error?: Error;
  /** History source (persistence pinning plus the plan-028 rename/delete
   *  actions) for shells that render their own switcher, e.g.
   *  `<FloatingChat.Root history={...}>`. */
  history: MagnusChatHistorySource;
  /** True while a persisted chat id is restoring/hydrating — render a
   *  skeleton meanwhile. Fresh sessions resolve immediately (no create
   *  round-trip; the id is minted client-side). */
  loading: boolean;
  /** Remount key for the chat component. */
  sessionKey: string;
}

/**
 * One hook for the whole conversation session (plan 019): composes the
 * models / skills / artifacts / feedback / history sources and owns the
 * session lifecycle every consumer used to hand-roll — restore the
 * persisted chat on mount, mint a fresh client-side id once that restore
 * settles (the server creates the row lazily on first send — never an
 * eager `chats.create`), keep the persistence seam pinned to the active
 * chat, and remount the transcript via `sessionKey`.
 *
 * ```tsx
 * const conversation = useMagnusConversation({ persistence });
 * return conversation.loading ? (
 *   <ChatHydrationSkeleton />
 * ) : (
 *   <MagnusChat key={conversation.sessionKey} {...conversation.chatProps} />
 * );
 * ```
 */
/**
 * Holds a prop's identity still for as long as its own fields are unchanged, so
 * an inline object from the host doesn't invalidate everything memoized
 * downstream. Nested objects are compared by identity — a host that varies one
 * of those still gets a fresh value, which is the safe direction to be wrong in.
 */
function useShallowStable<T extends object | undefined>(value: T): T {
  const ref = useRef(value);
  const previous = ref.current;
  if (previous === value) {
    return previous;
  }
  if (!(previous && value)) {
    ref.current = value;
    return value;
  }
  const previousKeys = Object.keys(previous);
  const same = previousKeys.length === Object.keys(value).length && previousKeys.every(key => Object.is((previous as Record<string, unknown>)[key], (value as Record<string, unknown>)[key]));
  if (!same) {
    ref.current = value;
  }
  return ref.current;
}
export function useMagnusConversation(options: UseMagnusConversationOptions = {}): UseMagnusConversationResult {
  const {
    dictation: dictationEnabled = true,
    liveCall: liveCallEnabled = true,
    persistence,
    turnOptions: hostTurnOptions
  } = options;
  const {
    client
  } = useMagnusClient();

  // Creating a conversation store is expensive in a way that is easy to miss:
  // it POSTs a fresh chat as soon as it's subscribed. Keying the memo on the
  // `persistence` prop itself would turn the common mistake of passing an
  // inline object into a render loop that mass-creates empty chats
  // server-side. So the store gets a stable adapter that forwards to whatever
  // the latest prop is, and only `client` can rebuild it.
  const persistenceRef = useRef(persistence);
  useEffect(() => {
    persistenceRef.current = persistence;
  }, [persistence]);
  const persistenceSeam = useMemo<MagnusChatIdPersistence>(() => ({
    readChatId: () => persistenceRef.current?.readChatId() ?? null,
    writeChatId: chatId => persistenceRef.current?.writeChatId(chatId)
  }), []);
  const store: ConversationStore = useMemo(() => createConversationStore({
    client,
    persistence: persistenceSeam
  }), [client, persistenceSeam]);
  // Cancels the history store's pending settle re-poll on unmount.
  useEffect(() => () => store.dispose(), [store]);
  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  const {
    defaultReasoningLevel,
    models: modelsSource,
    selectedModelId,
    supportsReasoningLevel
  } = useMagnusModels();
  // Like the web chat, the level belongs to the conversation: a new session
  // opens at the default rather than inheriting the last chat's depth.
  const [picked, setPicked] = useState<{
    level: ReasoningLevel;
    sessionKey: string;
  }>();
  const reasoningLevel = picked?.sessionKey === state.sessionKey ? picked.level : defaultReasoningLevel;
  const sessionKey = state.sessionKey;
  const models = useMemo<MagnusModelsSource>(() => ({
    ...modelsSource,
    onReasoningLevelChange: level => setPicked({
      level: level as ReasoningLevel,
      sessionKey
    }),
    reasoningLevel
  }), [modelsSource, reasoningLevel, sessionKey]);
  const skills = useMagnusSkillPicker();
  const artifacts = useMagnusArtifactPicker();
  const {
    feedback
  } = useMagnusFeedback(state.chatId);
  const {
    approvals
  } = useMagnusApprovals();
  const {
    uploads
  } = useMagnusUploads(state.chatId);
  const {
    dictation: dictationSource
  } = useMagnusDictation();
  const dictation = dictationEnabled ? dictationSource : undefined;
  const {
    liveCall
  } = useMagnusLiveCall({
    enabled: liveCallEnabled,
    openChat: chatId_0 => store.open(chatId_0),
    onChatMaterialized: () => store.onFirstStream(),
    onRecapSaved: () => {
      store.refresh().catch(() => undefined);
    }
  });

  // `turnOptions` is documented as read at send time, so hosts reasonably pass
  // an inline object. Keyed on that identity, the memo below would produce a
  // new `turnOptions` on every render, which flows into `chatProps` and defeats
  // its memo (and any consumer memoized on it). A field-level comparison keeps
  // the identity still for the scalar options hosts actually vary.
  const stableHostTurnOptions = useShallowStable(hostTurnOptions);
  const turnLevel = supportsReasoningLevel ? reasoningLevel : undefined;
  const turnOptions = useMemo<TurnOptions | undefined>(() => {
    const modelId = selectedModelId as TurnOptions["modelId"] | undefined;
    if (!(stableHostTurnOptions || modelId || turnLevel)) {
      return undefined;
    }
    // The picker's explicit selection wins on modelId and reasoningLevel; the
    // host owns everything else (workspace, capabilities).
    return {
      ...stableHostTurnOptions,
      ...(modelId ? {
        modelId
      } : {}),
      ...(turnLevel ? {
        reasoningLevel: turnLevel
      } : {})
    };
  }, [stableHostTurnOptions, selectedModelId, turnLevel]);

  // The store's history reads its list through getters on one long-lived
  // object; consumers memoize on identity, so hand out a snapshot per change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `state` changes on every list update, which is what re-snapshots the getters
  const history = useMemo<MagnusChatHistorySource>(() => ({
    ...store.history
  }), [store, state]);
  const chatProps = useMemo<MagnusConversationChatProps>(() => ({
    // The block's `activeStreamId` is `string | undefined` — null stays
    // a store-state detail.
    activeStreamId: state.activeStreamId ?? undefined,
    approvals,
    artifacts,
    chatId: state.chatId,
    dictation,
    feedback,
    history,
    initialMessages: state.initialMessages,
    liveCall,
    models,
    onFinish: store.onFinish,
    onFirstStream: store.onFirstStream,
    onStop: store.onStop,
    skills,
    turnOptions,
    uploads
  }), [approvals, artifacts, dictation, feedback, history, liveCall, models, skills, state, store, turnOptions, uploads]);
  return {
    chatId: state.chatId,
    chatProps,
    error: state.error,
    history,
    loading: state.loading,
    sessionKey: state.sessionKey
  };
}